<template>
  <div>
    <Head title="Bandeja de Tickets de Soporte" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800">Bandeja de Tickets de Soporte</h1>
        <p class="text-sm text-gray-500 mt-1">Gestiona y atiende las incidencias y consultas técnicas de los usuarios</p>
      </div>
    </div>

    <!-- Search & Filter Tool Bar -->
    <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-3">
        <!-- Buscador Rápido -->
        <div class="relative md:col-span-8 lg:col-span-8">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <icon name="search" class="w-4 h-4 text-gray-400" />
          </div>
          <input 
            v-model="form.search" 
            type="text" 
            class="block w-full pl-10 pr-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 placeholder-gray-400 text-sm transition-colors" 
            placeholder="Buscar por ID, asunto, categoría, cliente o correo..."
          />
        </div>

        <!-- Filtro por Estado -->
        <div class="md:col-span-3">
          <select 
            v-model="form.status"
            class="form-select w-full border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-gray-700 bg-white text-sm py-2"
          >
            <option :value="null">Todos los estados</option>
            <option value="Abierto">Abierto</option>
            <option value="En progreso">En progreso</option>
            <option value="Esperando respuesta del cliente">Esperando respuesta</option>
            <option value="Resuelto">Resuelto</option>
            <option value="Cerrado">Cerrado</option>
          </select>
        </div>

        <!-- Botón Limpiar Filtros -->
        <div class="md:col-span-1 flex items-center justify-end">
          <button 
            v-if="hasActiveFilters"
            type="button"
            class="w-full py-2 px-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1 text-sm"
            title="Limpiar Filtros"
            @click="reset"
          >
            <i class="fa fa-times" />
            <span class="md:hidden">Limpiar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Content Table Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Desktop Table -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-100 text-sm text-left">
          <thead>
            <tr class="bg-gray-50/50 text-gray-500 border-b border-gray-100">
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs w-24">Ticket</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Asunto & Solicitante</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Categoría</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Prioridad</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Estado</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Fecha</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="ticket in tickets.data" 
              :key="ticket.id" 
              class="hover:bg-orange-50/30 transition-colors"
            >
              <td class="py-3.5 px-6 whitespace-nowrap">
                <div class="font-mono font-bold text-gray-900">#{{ ticket.id }}</div>
              </td>
              <td class="py-3.5 px-6">
                <Link :href="`/admin/tickets/${ticket.id}`" class="font-semibold text-gray-900 hover:text-orange-600 transition-colors block">
                  {{ ticket.subject }}
                </Link>
                <div class="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                  <i class="fa fa-user-circle text-gray-400" />
                  <span>{{ getUserName(ticket.user) }}</span>
                  <span v-if="ticket.user?.email" class="text-gray-400">&bull; {{ ticket.user.email }}</span>
                </div>
              </td>
              <td class="py-3.5 px-6 whitespace-nowrap">
                <span class="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md font-medium">
                  {{ ticket.category || 'General' }}
                </span>
              </td>
              <td class="py-3.5 px-6 whitespace-nowrap">
                <span 
                  :class="{
                    'bg-red-50 text-red-700 border-red-200': ticket.priority === 'Urgente' || ticket.priority === 'Alta',
                    'bg-amber-50 text-amber-700 border-amber-200': ticket.priority === 'Media',
                    'bg-gray-50 text-gray-600 border-gray-200': ticket.priority === 'Baja' || !ticket.priority
                  }"
                  class="px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border"
                >
                  {{ ticket.priority || 'Normal' }}
                </span>
              </td>
              <td class="py-3.5 px-6 whitespace-nowrap">
                <span 
                  :class="{
                    'bg-blue-50 text-blue-700 border-blue-200': ticket.status === 'Abierto',
                    'bg-amber-50 text-amber-700 border-amber-200': ticket.status === 'En progreso',
                    'bg-orange-50 text-orange-700 border-orange-200': ticket.status === 'Esperando respuesta del cliente',
                    'bg-emerald-50 text-emerald-700 border-emerald-200': ticket.status === 'Resuelto',
                    'bg-gray-100 text-gray-600 border-gray-200': ticket.status === 'Cerrado' || !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
                  }"
                  class="px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border items-center gap-1"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full" :class="{
                      'bg-blue-500': ticket.status === 'Abierto',
                      'bg-amber-500': ticket.status === 'En progreso',
                      'bg-orange-500': ticket.status === 'Esperando respuesta del cliente',
                      'bg-emerald-500': ticket.status === 'Resuelto',
                      'bg-gray-400': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
                    }"
                  />
                  {{ ticket.status }}
                </span>
              </td>
              <td class="py-3.5 px-6 whitespace-nowrap text-xs text-gray-500">
                {{ formatDate(ticket.created_at) }}
              </td>
              <td class="py-3.5 px-6 whitespace-nowrap text-right">
                <Link 
                  :href="`/admin/tickets/${ticket.id}`" 
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 font-medium rounded-lg text-xs transition-colors"
                >
                  <i class="fa fa-headset" />
                  <span>Atender Ticket</span>
                </Link>
              </td>
            </tr>
            <tr v-if="!tickets.data?.length">
              <td colspan="7" class="py-16 text-center text-gray-400">
                <div class="flex flex-col items-center justify-center">
                  <div class="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-3">
                    <i class="fa fa-inbox text-2xl text-orange-400" />
                  </div>
                  <h3 class="text-base font-semibold text-gray-800 mb-1">No hay tickets disponibles</h3>
                  <p class="text-gray-500 text-sm max-w-sm mb-4">
                    {{ hasActiveFilters ? 'No se encontraron tickets con los filtros aplicados.' : 'No se han registrado solicitudes de tickets en el sistema.' }}
                  </p>
                  <button 
                    v-if="hasActiveFilters"
                    class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors" 
                    @click="reset"
                  >
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile List View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div v-for="ticket in tickets.data" :key="ticket.id" class="p-4 hover:bg-gray-50 transition-colors">
          <Link :href="`/admin/tickets/${ticket.id}`" class="block space-y-2.5">
            <div class="flex justify-between items-start">
              <div>
                <span class="font-mono font-bold text-gray-900 text-sm">#{{ ticket.id }}</span>
                <span class="text-xs text-gray-400 ml-2">{{ ticket.category }}</span>
              </div>
              <span 
                :class="{
                  'bg-blue-50 text-blue-700 border-blue-200': ticket.status === 'Abierto',
                  'bg-amber-50 text-amber-700 border-amber-200': ticket.status === 'En progreso',
                  'bg-orange-50 text-orange-700 border-orange-200': ticket.status === 'Esperando respuesta del cliente',
                  'bg-emerald-50 text-emerald-700 border-emerald-200': ticket.status === 'Resuelto',
                  'bg-gray-100 text-gray-600 border-gray-200': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
                }"
                class="px-2 py-0.5 text-xs font-semibold rounded-full border"
              >
                {{ ticket.status }}
              </span>
            </div>

            <h4 class="font-medium text-gray-900 text-sm">{{ ticket.subject }}</h4>
            <div class="text-xs text-gray-500">
              <i class="fa fa-user mr-1 text-gray-400" />{{ getUserName(ticket.user) }}
            </div>

            <div class="flex justify-between items-center text-xs text-gray-400 pt-1">
              <span>{{ formatDate(ticket.created_at) }}</span>
              <span class="text-orange-500 font-semibold flex items-center gap-1">
                Atender <i class="fa fa-chevron-right text-[10px]" />
              </span>
            </div>
          </Link>
        </div>

        <div v-if="!tickets.data?.length" class="p-8 text-center text-gray-400">
          <i class="fa fa-inbox text-3xl mb-2 text-orange-300" />
          <p class="text-sm">No hay tickets registrados</p>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="tickets.links && tickets.data.length > 0" class="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
        <pagination :links="tickets.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'

export default {
  name: 'AdminTicketsIndex',

  components: {
    Head,
    Icon,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    tickets: Object,
    filters: Object,
  },

  data() {
    return {
      form: {
        search: this.filters?.search || '',
        status: this.filters?.status || null,
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return !!(this.form.search || this.form.status)
    },
  },

  watch: {
    form: {
      deep: true,
      handler: debounce(function() {
        this.applyFilters()
      }, 300),
    },
  },

  methods: {
    applyFilters() {
      this.$inertia.get('/admin/tickets', pickBy(this.form), {
        preserveState: true,
        replace: true,
      })
    },

    getUserName(user) {
      if (!user) return 'Usuario General'
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }
      return user.name || 'Usuario'
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-VE', { 
        dateStyle: 'medium',
        timeStyle: 'short',
        hour12: false, 
      })
    },

    reset() {
      this.form = {
        search: '',
        status: null,
      }
    },
  },
}
</script>
